import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Plus, Search, Users, Lock, Pencil, Trash2, X, Loader2, Save } from 'lucide-react';
import { MAIL_API, mailAuth } from './mailUtils';
import { parseAddrs } from './RecipientInput';
import RichEditor from './RichEditor';

export const PLACEHOLDERS = [['{{first_name}}', 'Recipient first name'], ['{{name}}', 'Recipient full name'], ['{{company}}', 'Customer / company (from the job chip)'], ['{{wo}}', 'Work order number'], ['{{my_name}}', 'Your name']];

/** Replace {{tokens}} using the composer context (recipients, job chips, me). Unknown tokens are left visible so you notice them. */
export const fillTemplate = (html, ctx) => {
  const first = parseAddrs(ctx.to || '')[0];
  const name = first?.name || '';
  const wo = (ctx.chips || []).find(c => c.type === 'wo');
  const vals = {
    first_name: name.includes(',') ? name.split(',')[1].trim() : (name.split(' ')[0] || ''),
    name,
    company: wo?.customer || (ctx.chips || []).find(c => c.customer)?.customer || '',
    wo: wo?.ref || '',
    my_name: ctx.myName || '',
  };
  return (html || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (vals[k] !== undefined && vals[k] !== '' ? vals[k] : `<span style="color: #dc2626"><strong>{{${k}}}</strong></span>`));
};

/** Undo the red {{placeholder}} highlight so a body saved as a template stays clean. */
export const unhighlight = (html) => (html || '').replace(/<span style="color:\s*#dc2626;?"><strong>(\{\{\s*\w+\s*\}\})<\/strong><\/span>/g, '$1');

export const useTemplates = () => {
  const [list, setList] = useState(null);
  const load = () => axios.get(`${MAIL_API}/templates`, mailAuth()).then(r => setList(r.data.templates || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  return { list, reload: load };
};

/** Dropdown in the composer: pick a canned reply (fills placeholders), save the current body as one, or manage them. */
export const TemplatePicker = ({ ctx, onInsert, currentBody, currentSubject, compact = false }) => {
  const { list, reload } = useTemplates();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [manage, setManage] = useState(false);
  const [saveNew, setSaveNew] = useState(false);
  const filtered = useMemo(() => (list || []).filter(t => !q || t.name.toLowerCase().includes(q.toLowerCase())), [list, q]);
  const pick = (t) => {
    onInsert({ body_html: fillTemplate(t.body_html, ctx), subject: t.subject ? fillTemplate(t.subject, ctx) : '', id: t.id, name: t.name });
    setOpen(false);
  };
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} data-testid={compact ? "tpl-open-quick" : "tpl-open"} title="Canned replies / templates"
        className={`inline-flex items-center gap-1.5 rounded-lg text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 ${compact ? 'p-1.5' : 'px-2.5 py-2 text-xs font-semibold'}`}>
        <FileText className="w-4 h-4" />{!compact && 'Templates'}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[9100]" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full mb-1 left-0 z-[9110] w-80 rounded-xl bg-white border border-slate-200 shadow-2xl overflow-hidden" data-testid={compact ? "tpl-menu-quick" : "tpl-menu"}>
            <div className="p-2 border-b border-slate-100 relative">
              <Search className="w-3.5 h-3.5 absolute left-4 top-4 text-slate-400" />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Find a template…" className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="tpl-search" />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {list === null && <p className="px-3 py-2 text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading…</p>}
              {filtered.map(t => (
                <button key={t.id} type="button" onClick={() => pick(t)} className="w-full text-left px-3 py-2 hover:bg-sky-50 flex items-start gap-2" data-testid={`tpl-pick${compact ? '-quick' : ''}-${t.id}`}>
                  {t.shared ? <Users className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" /> : <Lock className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />}
                  <span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-slate-800 truncate">{t.name}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{(t.body_html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80)}</span></span>
                  {t.uses > 0 && <span className="text-[10px] text-slate-400 shrink-0">{t.uses}×</span>}
                </button>
              ))}
              {list && filtered.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No templates match.</p>}
            </div>
            <div className="border-t border-slate-100 p-1.5 flex items-center gap-1 text-xs">
              <button type="button" onClick={() => { setOpen(false); setSaveNew(true); }} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-slate-100 text-slate-700 font-semibold" data-testid="tpl-save-current"><Save className="w-3.5 h-3.5" />Save this as template</button>
              <button type="button" onClick={() => { setOpen(false); setManage(true); }} className="ml-auto inline-flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-slate-100 text-slate-700 font-semibold" data-testid="tpl-manage"><Pencil className="w-3.5 h-3.5" />Manage</button>
            </div>
          </div>
        </>
      )}
      {manage && <TemplateManager onClose={() => { setManage(false); reload(); }} />}
      {saveNew && <TemplateEditor initial={{ name: '', subject: currentSubject || '', body_html: unhighlight(currentBody || ''), shared: false }} onClose={() => setSaveNew(false)} onSaved={() => { setSaveNew(false); reload(); }} />}
    </div>
  );
};

const TemplateEditor = ({ initial, onClose, onSaved }) => {
  const [t, setT] = useState(initial);
  const [busy, setBusy] = useState(false);
  const edRef = useRef(null);
  const save = async () => {
    if (!t.name.trim()) return toast.error('Give it a name');
    if (!(t.body_html || '').replace(/<[^>]+>/g, '').trim()) return toast.error('Write the template body');
    setBusy(true);
    try {
      const body = { name: t.name, subject: t.subject, body_html: unhighlight(t.body_html), shared: t.shared };
      const r = t.id ? await axios.patch(`${MAIL_API}/templates/${t.id}`, body, mailAuth()) : await axios.post(`${MAIL_API}/templates`, body, mailAuth());
      toast.success(t.id ? 'Template updated' : 'Template saved');
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save template'); }
    setBusy(false);
  };
  return createPortal(
    <div className="fixed inset-0 z-[9300] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="tpl-editor">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-black uppercase tracking-wider text-slate-900">{t.id ? 'Edit template' : 'New template'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="tpl-editor-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-slate-600">Name<input value={t.name} onChange={e => setT({ ...t, name: e.target.value })} placeholder="e.g. Quote follow-up" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg" data-testid="tpl-name" /></label>
            <label className="text-xs font-semibold text-slate-600">Subject (optional)<input value={t.subject || ''} onChange={e => setT({ ...t, subject: e.target.value })} placeholder="Leave blank to keep the current subject" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg" data-testid="tpl-subject" /></label>
          </div>
          <RichEditor ref={edRef} value={t.body_html} onChange={v => setT(x => ({ ...x, body_html: v }))} placeholder="Template body…" minHeight={200} testid="tpl-body" />
          <div className="flex flex-wrap gap-1.5 items-center text-[11px] text-slate-500">
            <span>Insert:</span>
            {PLACEHOLDERS.map(([tok, hint]) => <button key={tok} type="button" onClick={() => edRef.current?.insertHtml(tok)} title={hint} className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-sky-100 font-mono text-slate-700" data-testid={`tpl-ph-${tok.replace(/\W/g, '')}`}>{tok}</button>)}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!t.shared} onChange={e => setT({ ...t, shared: e.target.checked })} className="h-4 w-4 accent-sky-600" data-testid="tpl-shared" />Share with the whole team</label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 text-white font-bold" data-testid="tpl-save">{busy ? 'Saving…' : 'Save template'}</button>
        </div>
      </div>
    </div>, document.body);
};

export const TemplateManager = ({ onClose }) => {
  const { list, reload } = useTemplates();
  const [editing, setEditing] = useState(null);
  const del = async (t) => {
    if (!window.confirm(`Delete “${t.name}”?`)) return;
    try { await axios.delete(`${MAIL_API}/templates/${t.id}`, mailAuth()); toast.success('Deleted'); reload(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[9200] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="tpl-manager">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div><h3 className="text-sm font-black uppercase tracking-wider text-slate-900">Canned replies</h3><p className="text-[11px] text-slate-500">Shared ones show for everyone; private ones only for you.</p></div>
          <div className="flex items-center gap-1">
            <button onClick={() => setEditing({ name: '', subject: '', body_html: '', shared: false })} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-bold" data-testid="tpl-new"><Plus className="w-3.5 h-3.5" />New</button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="tpl-manager-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <ul className="overflow-y-auto divide-y divide-slate-100">
          {(list || []).map(t => (
            <li key={t.id} className="px-5 py-3 flex items-start gap-3" data-testid={`tpl-row-${t.id}`}>
              {t.shared ? <Users className="w-4 h-4 text-slate-400 mt-0.5" /> : <Lock className="w-4 h-4 text-slate-400 mt-0.5" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-900">{t.name} <span className="text-[11px] font-normal text-slate-400">· {t.owner_name}{t.uses ? ` · used ${t.uses}×` : ''}</span></p>
                <p className="text-xs text-slate-500 truncate">{(t.body_html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120)}</p>
              </div>
              {t.editable && <button onClick={() => setEditing(t)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title="Edit" data-testid={`tpl-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>}
              {t.editable && <button onClick={() => del(t)} className="p-1.5 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`tpl-del-${t.id}`}><Trash2 className="w-4 h-4" /></button>}
            </li>
          ))}
          {list && list.length === 0 && <li className="px-5 py-6 text-sm text-slate-400 text-center">No templates yet.</li>}
        </ul>
      </div>
      {editing && <TemplateEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>, document.body);
};
